import { ArrowDownward } from "@mui/icons-material";
import {
    TimelineConnector,
    TimelineContent,
    TimelineDot,
    TimelineItem as MUITimelineItem,
    TimelineOppositeContent,
    TimelineSeparator,
} from "@mui/lab";
import MUITimeline from "@mui/lab/Timeline";
import { IconButton, Paper, Stack, Tooltip, Typography } from "@mui/material";
import React, { useRef } from "react";
import theme from "Theme/AppTheme";

export interface TimelineItem {
    id: string;
    time: string;
    icon: React.ReactNode;
    content: string;
    contentHeader?: string;
    emphasized?: boolean;
    variant?: "filled" | "outlined";
    color?: React.CSSProperties["color"];
}

interface TimelineProps {
    items: TimelineItem[];
    header?: React.ReactNode;
}

export default function Timeline(props: TimelineProps) {
    const timelineBottomTarget = useRef<HTMLDivElement>(null);
    const header = props.header || "Timeline";

    return (
        <Paper
            variant="outlined"
            sx={{
                minWidth: 0,
                borderColor: theme.palette.gray[200],
                borderRadius: 2,
                overflow: "hidden",
            }}
        >
            {props.items.length > 0 && (
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={1}
                    sx={{
                        px: 2,
                        py: 1.5,
                        borderBottom: `1px solid ${theme.palette.gray[200]}`,
                    }}
                >
                    <Typography
                        variant="h6"
                        noWrap
                        title={typeof header === "string" ? header : undefined}
                    >
                        {header}
                    </Typography>
                    <Tooltip title="Jump to the oldest event">
                        <IconButton
                            aria-label="Jump to the oldest timeline event"
                            onClick={() =>
                                timelineBottomTarget.current?.scrollIntoView({
                                    behavior: "smooth",
                                    block: "nearest",
                                })
                            }
                            size="small"
                        >
                            <ArrowDownward />
                        </IconButton>
                    </Tooltip>
                </Stack>
            )}

            <MUITimeline sx={{ m: 0, px: { xs: 1, md: 2 }, py: 2 }}>
                {props.items.map(
                    (
                        {
                            id,
                            time,
                            icon,
                            content,
                            contentHeader,
                            emphasized = false,
                            variant = "outlined",
                            color = "gray",
                        },
                        idx,
                    ) => {
                        const isFirstItem = idx === 0;
                        const isLastItem = idx === props.items.length - 1;
                        return (
                            <MUITimelineItem key={id} sx={{ minHeight: 76 }}>
                                <TimelineOppositeContent
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "center",
                                        flex: {
                                            xs: "0 0 145px",
                                            sm: "0 0 210px",
                                        },
                                        px: { xs: 0.5, sm: 1.5 },
                                    }}
                                >
                                    <Typography
                                        align="right"
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ lineHeight: 1.4 }}
                                    >
                                        {time}
                                    </Typography>
                                </TimelineOppositeContent>
                                <TimelineSeparator>
                                    {!isFirstItem && (
                                        <TimelineConnector
                                            sx={{
                                                backgroundColor:
                                                    theme.palette.gray[200],
                                            }}
                                        />
                                    )}
                                    <TimelineDot
                                        variant={variant}
                                        sx={{
                                            color:
                                                variant === "outlined"
                                                    ? color
                                                    : theme.palette.white.main,
                                            backgroundColor:
                                                variant === "filled"
                                                    ? color
                                                    : theme.palette.white.main,
                                            borderColor: color,
                                            boxShadow: "none",
                                        }}
                                    >
                                        {icon}
                                    </TimelineDot>
                                    {!isLastItem && (
                                        <TimelineConnector
                                            sx={{
                                                backgroundColor:
                                                    theme.palette.gray[200],
                                            }}
                                        />
                                    )}
                                </TimelineSeparator>
                                <TimelineContent
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "center",
                                        alignItems: "flex-start",
                                        minWidth: 0,
                                        px: { xs: 1, sm: 2 },
                                        textAlign: "left",
                                    }}
                                >
                                    {contentHeader && (
                                        <Typography
                                            variant="caption"
                                            component="span"
                                            color="text.secondary"
                                            sx={{ fontWeight: 500, mb: 0.25 }}
                                        >
                                            {contentHeader}
                                        </Typography>
                                    )}
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            fontWeight: emphasized ? 600 : 400,
                                        }}
                                    >
                                        {content}
                                    </Typography>
                                </TimelineContent>
                            </MUITimelineItem>
                        );
                    },
                )}
            </MUITimeline>
            <div ref={timelineBottomTarget} />
        </Paper>
    );
}
